import { ShoppingCart } from 'lucide-react';

const PartnerCatalog = ({
  approvedBrands,
  brandDiscounts,
  filteredProducts,
  searchTerm,
  setSearchTerm,
  selectedBrand,
  setSelectedBrand,
  selectedCategory,
  setSelectedCategory,
  getDiscountedPrice,
  addToCart,
}) => {
  return (
    <div className="space-y-6" data-testid="partner-catalog">
      {/* Approved Brands Notice */}
      {approvedBrands.length > 0 && (
        <div className="bg-ewss-cyan/10 border border-ewss-cyan/30 rounded-xl p-4">
          <p className="text-white font-semibold mb-1">Your Approved Brands:</p>
          <p className="text-ewss-cyan">{approvedBrands.join(', ')}</p>
          <p className="text-gray-400 text-sm mt-2">
            Discounts:{' '}
            {Object.entries(brandDiscounts)
              .map(([brand, discount]) => `${brand}: ${discount}%`)
              .join(' | ')}
          </p>
        </div>
      )}

      {approvedBrands.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <p className="text-yellow-400 font-semibold">No brands approved yet</p>
          <p className="text-gray-400 text-sm mt-1">
            Please contact admin to get brand access approved.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Search Products</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by SKU or description..."
              className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
              data-testid="partner-catalog-search-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Brand</label>
            <select
              value={selectedBrand}
              onChange={(e) => setSelectedBrand(e.target.value)}
              className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
              data-testid="partner-catalog-brand-select"
            >
              <option value="All">All Brands</option>
              {approvedBrands.map((brand) => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Filter by Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white focus:outline-none focus:border-ewss-cyan"
              data-testid="partner-catalog-category-select"
            >
              <option value="All">All Categories</option>
              {[...new Set(filteredProducts.map((p) => p.category))].sort().map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-12 text-gray-400" data-testid="partner-catalog-empty">
          {approvedBrands.length === 0 ? 'No brands approved yet' : 'No products found'}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const { baseIncl, partnerExcl, partnerIncl, discount } = getDiscountedPrice(product);

            return (
              <div
                key={product.id}
                className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700 hover:border-ewss-cyan/50 transition-all"
                data-testid={`partner-product-card-${product.sku}`}
              >
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-bold text-white">{product.product_name}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        product.stock_status === 'In Stock'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {product.stock_status}
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm mb-1">SKU: {product.sku}</p>
                  <p className="text-gray-400 text-sm mb-1">
                    {product.brand} • {product.category}
                  </p>
                  {product.subcategory && (
                    <p className="text-gray-500 text-xs">Subcategory: {product.subcategory}</p>
                  )}
                  {product.description && (
                    <p className="text-gray-300 text-sm mt-2">{product.description}</p>
                  )}
                </div>

                {/* Pricing — always show BOTH excl & incl VAT */}
                <div className="mb-4 space-y-1">
                  {discount > 0 && (
                    <div className="flex items-center justify-between text-xs mb-2">
                      <span className="text-gray-500">List (incl VAT):</span>
                      <span className="text-gray-500 line-through">
                        R{' '}
                        {baseIncl.toLocaleString('en-ZA', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">Your Price (excl VAT):</span>
                    <span className="text-white font-semibold">
                      R{' '}
                      {partnerExcl.toLocaleString('en-ZA', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Your Price (incl VAT):</span>
                    <span className="text-ewss-cyan font-bold text-lg">
                      R{' '}
                      {partnerIncl.toLocaleString('en-ZA', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {discount > 0 && (
                    <div className="text-center py-1 bg-ewss-cyan/20 rounded mt-2">
                      <span className="text-ewss-cyan text-xs font-semibold">
                        {discount}% {product.brand} Discount applied
                      </span>
                    </div>
                  )}
                  <p className="text-gray-500 text-xs text-center pt-1">per {product.unit}</p>
                </div>

                <button
                  onClick={() => addToCart(product)}
                  className="w-full px-4 py-2 bg-ewss-cyan text-ewss-navy font-semibold rounded-lg hover:bg-ewss-cyan/90 transition-colors flex items-center justify-center gap-2"
                  data-testid={`partner-add-to-cart-${product.sku}`}
                >
                  <ShoppingCart size={18} />
                  Add to Cart
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartnerCatalog;
